import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { Send, User, Building2, CheckCircle } from 'lucide-react';

const ContactForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('individual');

  // Individual form state
  const [individualForm, setIndividualForm] = useState({
    name: '',
    email: '',
    phone: '',
    inquiry_type: 'consultation',
    destination_country: 'Germany',
    current_country: '',
    moving_timeline: '',
    message: ''
  });

  // Company form state
  const [companyForm, setCompanyForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    inquiry_type: 'consultation',
    department: '',
    employee_count: '',
    specific_needs: '',
    message: ''
  });

  const handleIndividualChange = (field, value) => {
    setIndividualForm(prev => ({ ...prev, [field]: value }));
  };

  const handleCompanyChange = (field, value) => {
    setCompanyForm(prev => ({ ...prev, [field]: value }));
  };

  const handleIndividualSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/forms/expat-inquiry`,
        individualForm
      );
      
      console.log('Individual Form Response:', response.data);
      
      toast.success('Thank you! We will contact you shortly.', {
        description: 'Your inquiry has been received successfully.',
      });

      // Reset form
      setIndividualForm({
        name: '',
        email: '',
        phone: '',
        inquiry_type: 'consultation',
        destination_country: 'Germany',
        current_country: '',
        moving_timeline: '',
        message: ''
      });
    } catch (error) {
      console.error('Submission error:', error);
      const errorMsg = error.response?.data?.detail || 'Please try again or contact us directly at nicklas@gr-prosperity.de';
      toast.error('Submission failed', {
        description: errorMsg,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCompanySubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/forms/company-inquiry`,
        companyForm
      );
      
      console.log('Company Form Response:', response.data);
      
      toast.success('Thank you! We will contact you shortly.', {
        description: 'Your inquiry has been received successfully.',
      });

      // Reset form
      setCompanyForm({
        name: '',
        email: '',
        phone: '',
        company: '',
        inquiry_type: 'consultation',
        department: '',
        employee_count: '',
        specific_needs: '',
        message: ''
      });
    } catch (error) {
      console.error('Submission error:', error);
      const errorMsg = error.response?.data?.detail || 'Please try again or contact us directly at nicklas@gr-prosperity.de';
      toast.error('Submission failed', {
        description: errorMsg,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Let's Start Your
            <span className="text-orange-500"> Journey Together</span>
          </h2>
          <p className="text-xl text-gray-600">
            Schedule your free 45-minute consultation or request more information.
          </p>
        </div>

        {/* Form Tabs */}
        <div className="bg-white rounded-2xl shadow-2xl border-2 border-gray-100 overflow-hidden">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full grid grid-cols-2 bg-gray-50 p-2 rounded-none">
              <TabsTrigger
                value="individual"
                className="data-[state=active]:bg-orange-500 data-[state=active]:text-white transition-all duration-300 py-4 text-base font-semibold rounded-lg"
              >
                <User className="w-5 h-5 mr-2" />
                For Individuals
              </TabsTrigger>
              <TabsTrigger
                value="company"
                className="data-[state=active]:bg-orange-500 data-[state=active]:text-white transition-all duration-300 py-4 text-base font-semibold rounded-lg"
              >
                <Building2 className="w-5 h-5 mr-2" />
                For Companies
              </TabsTrigger>
            </TabsList>

            {/* Individual Form */}
            <TabsContent value="individual" className="p-8">
              <form onSubmit={handleIndividualSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="ind-name">Full Name *</Label>
                    <Input
                      id="ind-name"
                      type="text"
                      placeholder="John Doe"
                      required
                      value={individualForm.name}
                      onChange={(e) => handleIndividualChange('name', e.target.value)}
                      className="border-2 focus:border-orange-500 transition-colors"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ind-email">Email Address *</Label>
                    <Input
                      id="ind-email"
                      type="email"
                      placeholder="john@example.com"
                      required
                      value={individualForm.email}
                      onChange={(e) => handleIndividualChange('email', e.target.value)}
                      className="border-2 focus:border-orange-500 transition-colors"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="ind-phone">Phone Number</Label>
                    <Input
                      id="ind-phone"
                      type="tel"
                      placeholder="+49 123 456789"
                      value={individualForm.phone}
                      onChange={(e) => handleIndividualChange('phone', e.target.value)}
                      className="border-2 focus:border-orange-500 transition-colors"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ind-inquiry-type">Inquiry Type *</Label>
                    <Select
                      value={individualForm.inquiry_type}
                      onValueChange={(value) => handleIndividualChange('inquiry_type', value)}
                    >
                      <SelectTrigger id="ind-inquiry-type" className="border-2 focus:border-orange-500">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="consultation">General Consultation</SelectItem>
                        <SelectItem value="relocation">Relocation Support</SelectItem>
                        <SelectItem value="visa">Visa & Immigration</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="ind-current-country">Current Country</Label>
                    <Input
                      id="ind-current-country"
                      type="text"
                      placeholder="United States"
                      value={individualForm.current_country}
                      onChange={(e) => handleIndividualChange('current_country', e.target.value)}
                      className="border-2 focus:border-orange-500 transition-colors"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ind-timeline">Moving Timeline</Label>
                    <Input
                      id="ind-timeline"
                      type="text"
                      placeholder="3-6 months"
                      value={individualForm.moving_timeline}
                      onChange={(e) => handleIndividualChange('moving_timeline', e.target.value)}
                      className="border-2 focus:border-orange-500 transition-colors"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="ind-message">Additional Information</Label>
                  <Textarea
                    id="ind-message"
                    placeholder="Tell us about your situation and what you're looking for..."
                    rows={4}
                    value={individualForm.message}
                    onChange={(e) => handleIndividualChange('message', e.target.value)}
                    className="border-2 focus:border-orange-500 transition-colors resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white text-lg py-6 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Schedule Free Consultation
                    </>
                  )}
                </Button>

                <p className="text-sm text-gray-500 text-center">
                  By submitting, you agree to our privacy policy. We'll respond within 24 hours.
                </p>
              </form>
            </TabsContent>

            {/* Company Form */}
            <TabsContent value="company" className="p-8">
              <form onSubmit={handleCompanySubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="comp-name">Contact Name *</Label>
                    <Input
                      id="comp-name"
                      type="text"
                      placeholder="Jane Smith"
                      required
                      value={companyForm.name}
                      onChange={(e) => handleCompanyChange('name', e.target.value)}
                      className="border-2 focus:border-orange-500 transition-colors"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="comp-company">Company Name *</Label>
                    <Input
                      id="comp-company"
                      type="text"
                      placeholder="Acme Corporation"
                      required
                      value={companyForm.company}
                      onChange={(e) => handleCompanyChange('company', e.target.value)}
                      className="border-2 focus:border-orange-500 transition-colors"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="comp-email">Email Address *</Label>
                    <Input
                      id="comp-email"
                      type="email"
                      placeholder="jane@company.com"
                      required
                      value={companyForm.email}
                      onChange={(e) => handleCompanyChange('email', e.target.value)}
                      className="border-2 focus:border-orange-500 transition-colors"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="comp-phone">Phone Number</Label>
                    <Input
                      id="comp-phone"
                      type="tel"
                      placeholder="+49 123 456789"
                      value={companyForm.phone}
                      onChange={(e) => handleCompanyChange('phone', e.target.value)}
                      className="border-2 focus:border-orange-500 transition-colors"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="comp-department">Department</Label>
                    <Input
                      id="comp-department"
                      type="text"
                      placeholder="HR / Talent Acquisition"
                      value={companyForm.department}
                      onChange={(e) => handleCompanyChange('department', e.target.value)}
                      className="border-2 focus:border-orange-500 transition-colors"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="comp-employees">Number of Employees</Label>
                    <Input
                      id="comp-employees"
                      type="number"
                      placeholder="500"
                      value={companyForm.employee_count}
                      onChange={(e) => handleCompanyChange('employee_count', e.target.value)}
                      className="border-2 focus:border-orange-500 transition-colors"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="comp-inquiry-type">Inquiry Type *</Label>
                  <Select
                    value={companyForm.inquiry_type}
                    onValueChange={(value) => handleCompanyChange('inquiry_type', value)}
                  >
                    <SelectTrigger id="comp-inquiry-type" className="border-2 focus:border-orange-500">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="consultation">General Consultation</SelectItem>
                      <SelectItem value="recruitment">Recruitment Support</SelectItem>
                      <SelectItem value="training">Training & Development</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="comp-needs">Specific Needs & Challenges</Label>
                  <Textarea
                    id="comp-needs"
                    placeholder="Tell us about your company's needs, number of expats you're relocating, and any specific challenges..."
                    rows={4}
                    value={companyForm.specific_needs}
                    onChange={(e) => handleCompanyChange('specific_needs', e.target.value)}
                    className="border-2 focus:border-orange-500 transition-colors resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white text-lg py-6 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Request Partnership Information
                    </>
                  )}
                </Button>

                <p className="text-sm text-gray-500 text-center">
                  By submitting, you agree to our privacy policy. We'll respond within 24 hours.
                </p>
              </form>
            </TabsContent>
          </Tabs>
        </div>

        {/* Contact Information */}
        <div className="mt-12 grid md:grid-cols-3 gap-6 text-center">
          <div className="p-6 bg-gray-50 rounded-xl">
            <div className="text-orange-500 font-semibold mb-2">Email</div>
            <a href="mailto:kontakt@gr-prosperity.de" className="text-gray-700 hover:text-orange-500 transition-colors">
              kontakt@gr-prosperity.de
            </a>
          </div>
          <div className="p-6 bg-gray-50 rounded-xl">
            <div className="text-orange-500 font-semibold mb-2">Phone</div>
            <a href="tel:+498105775720" className="text-gray-700 hover:text-orange-500 transition-colors">
              +49 8105 7757206
            </a>
          </div>
          <div className="p-6 bg-gray-50 rounded-xl">
            <div className="text-orange-500 font-semibold mb-2">Address</div>
            <div className="text-gray-700">
              Am Hochacker 48<br />81827 München
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
